import { toast } from 'sonner'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import axiosInstance, { endpoints } from '@/lib/axios'
import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import { MoreHorizontal, Plus, GripVertical, Layers } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'

import type { ClipCategory } from '../clip-category.types'

import ClipCategoryFormDialog from '../components/clip-category-form-dialog'

// ----------------------------------------------------------------------

type FormValues = {
  label: string
  isEnabled: boolean
}

// ----------------------------------------------------------------------

interface SortableRowProps {
  cat: ClipCategory
  onToggle: (cat: ClipCategory, value: boolean) => void
  onEdit: (cat: ClipCategory) => void
  onDelete: (cat: ClipCategory) => void
}

function SortableRow({ cat, onToggle, onEdit, onDelete }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cat.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative' as const,
  }

  return (
    <TableRow ref={setNodeRef} style={style}>
      {/* grip handle */}
      <TableCell className="w-10">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="flex items-center justify-center p-1 rounded cursor-grab hover:bg-muted active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="ลากเพื่อเรียงลำดับ"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>

      {/* order */}
      <TableCell className="w-16">
        <span className="text-sm tabular-nums text-muted-foreground">
          {cat.order}
        </span>
      </TableCell>

      {/* label */}
      <TableCell className="font-medium">{cat.label}</TableCell>

      {/* isEnabled switch */}
      <TableCell className="w-32">
        <Switch
          checked={cat.isEnabled}
          onCheckedChange={(v) => onToggle(cat, v)}
          aria-label={`สถานะ ${cat.label}`}
        />
      </TableCell>

      {/* actions dropdown */}
      <TableCell className="w-24 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">จัดการ</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(cat)}>
              แก้ไข
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              onClick={() => onDelete(cat)}
            >
              ลบ
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}

// ----------------------------------------------------------------------

export default function ClipCategoriesView() {
  const [categories, setCategories] = useState<ClipCategory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [formDialog, setFormDialog] = useState<{
    open: boolean
    category?: ClipCategory
  }>({ open: false })
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    category: ClipCategory | null
  }>({ open: false, category: null })
  const [deleteLoading, setDeleteLoading] = useState(false)

  const sorted = [...categories].sort((a, b) => a.order - b.order)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // ── fetch ─────────────────────────────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    setLoadError(false)
    try {
      const res = await axiosInstance.get(endpoints.clipCategories.list)
      const items: ClipCategory[] = res.data ?? []
      setCategories(items)
    } catch (err) {
      console.error('fetchCategories error', err)
      toast.error('โหลดหมวดหมู่ล้มเหลว')
      setLoadError(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  // ── drag & drop reorder ───────────────────────────────────────────────
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = sorted.findIndex((c) => c.id === active.id)
    const newIndex = sorted.findIndex((c) => c.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    // Build new sorted list with updated order values
    const reordered = [...sorted]
    const [moved] = reordered.splice(oldIndex, 1)
    reordered.splice(newIndex, 0, moved)
    const withNewOrder = reordered.map((c, i) => ({ ...c, order: i + 1 }))

    // Optimistic update
    setCategories(withNewOrder)

    try {
      await axiosInstance.patch(endpoints.clipCategories.reorder, {
        items: withNewOrder.map((c) => ({ id: c.id, order: c.order })),
      })
      toast.success('เรียงลำดับแล้ว')
    } catch (err) {
      console.error('reorder error', err)
      toast.error('เรียงลำดับล้มเหลว')
      // Rollback
      fetchCategories()
    }
  }

  // ── toggle enabled ────────────────────────────────────────────────────
  const handleToggle = async (cat: ClipCategory, value: boolean) => {
    setCategories((prev) =>
      prev.map((c) =>
        c.id === cat.id
          ? { ...c, isEnabled: value, updatedAt: new Date().toISOString() }
          : c,
      ),
    )
    try {
      await axiosInstance.patch(endpoints.clipCategories.update(cat.id), {
        isEnabled: value,
      })
      toast.success('อัปเดตสถานะแล้ว')
    } catch (err) {
      console.error('toggle error', err)
      toast.error('อัปเดตสถานะล้มเหลว')
      fetchCategories()
    }
  }

  // ── delete ────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteDialog.category) return
    setDeleteLoading(true)
    try {
      await axiosInstance.delete(
        endpoints.clipCategories.delete(deleteDialog.category.id),
      )
      toast.success('ลบหมวดหมู่แล้ว')
      await fetchCategories()
    } catch (err) {
      console.error('delete category error', err)
      toast.error('ลบหมวดหมู่ล้มเหลว')
    } finally {
      setDeleteLoading(false)
      setDeleteDialog({ open: false, category: null })
    }
  }

  // ── create / edit ─────────────────────────────────────────────────────
  const handleFormSubmit = async (values: FormValues) => {
    try {
      if (formDialog.category) {
        await axiosInstance.patch(
          endpoints.clipCategories.update(formDialog.category.id),
          values,
        )
        toast.success('แก้ไขหมวดหมู่แล้ว')
      } else {
        await axiosInstance.post(endpoints.clipCategories.create, values)
        toast.success('เพิ่มหมวดหมู่แล้ว')
      }
      setFormDialog({ open: false })
      await fetchCategories()
    } catch (err) {
      console.error('form submit error', err)
      toast.error('บันทึกหมวดหมู่ล้มเหลว')
    }
  }

  // ── skeleton ──────────────────────────────────────────────────────────
  const renderSkeleton = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <div className="h-6 w-6 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
          <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
          <div className="h-6 w-10 bg-gray-200 rounded-full animate-pulse" />
        </TableCell>
        <TableCell />
      </TableRow>
    ))

  return (
    <div className="w-full space-y-6">
      {/* Header Card */}
      <Card>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-black">
                จัดการหมวดหมู่คลิป
              </h2>
              <Button size="sm" onClick={() => setFormDialog({ open: true })}>
                <Plus className="h-4 w-4 mr-1" />
                เพิ่มหมวดหมู่
              </Button>
            </div>
            <Separator />
            <p className="text-sm text-muted-foreground">
              {isLoading ? '...' : `${sorted.length} หมวดหมู่`}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="rounded-lg border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead className="w-16">ลำดับ</TableHead>
              <TableHead>ชื่อหมวดหมู่</TableHead>
              <TableHead className="w-32">เปิดใช้งาน</TableHead>
              <TableHead className="w-24 text-right">จัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              renderSkeleton()
            ) : loadError ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="flex flex-col items-center gap-3 py-10 text-center">
                    <Layers className="h-10 w-10 text-gray-300" />
                    <p className="text-sm text-muted-foreground">
                      โหลดข้อมูลล้มเหลว
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setIsLoading(true)
                        fetchCategories()
                      }}
                    >
                      ลองใหม่
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : sorted.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="flex flex-col items-center gap-3 py-10 text-center">
                    <Layers className="h-10 w-10 text-gray-300" />
                    <p className="text-base text-muted-foreground">
                      ยังไม่มีหมวดหมู่ในระบบ
                    </p>
                    <Button
                      size="sm"
                      onClick={() => setFormDialog({ open: true })}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      เพิ่มหมวดหมู่
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sorted.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {sorted.map((cat) => (
                    <SortableRow
                      key={cat.id}
                      cat={cat}
                      onToggle={handleToggle}
                      onEdit={(c) => setFormDialog({ open: true, category: c })}
                      onDelete={(c) =>
                        setDeleteDialog({ open: true, category: c })
                      }
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Form Dialog */}
      <ClipCategoryFormDialog
        open={formDialog.open}
        onOpenChange={(open) => setFormDialog((s) => ({ ...s, open }))}
        category={formDialog.category}
        onSubmit={handleFormSubmit}
      />

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, category: null })}
        title="ยืนยันการลบ"
        description={`คุณต้องการลบหมวดหมู่ "${deleteDialog.category?.label}" หรือไม่? การดำเนินการนี้ไม่สามารถยกเลิกได้`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />
    </div>
  )
}
